// Where a declared field's answer is saved.
//
// CONSUMER: internal-ui (the Cleanup / Data Needed card) — browser-safe,
// no db imports, the same way fix.ts is.
//
// A detector that declares `type-it-in` fields names ONE writer from the
// closed CleanupFieldWriter list. This registry says what that name means:
// the record endpoint the record page itself saves through, so company
// scope, validation, versions and audit are the endpoint's own and the
// card adds no second write path. tests/unit/cleanup-answer-writers.test.ts
// fails the build when a writer is named in fix.ts and not here, or here
// and not there.
//
// The wire encoding per input kind lives here too, next to the endpoint it
// feeds, so a "yesno" field lands as the 0/1 the loan route accepts.

import type { CleanupAskedField, CleanupFieldWriter } from "./fix";

export interface AnswerWriter {
  /** Plain words for the card's fine print: what the save touches. */
  writes: string;
  method: "PATCH";
  path: (writerId: string) => string;
}

export const ANSWER_WRITERS: Readonly<Record<CleanupFieldWriter, AnswerWriter>> = {
  "mortgage-loan-details": {
    writes: "the loan record",
    method: "PATCH",
    path: (loanId) => `/api/mortgage-loans/${encodeURIComponent(loanId)}/details`,
  },
  // The contact page's own save (versioned, field-audited, and it re-checks
  // the contact's cleanup items before it answers).
  contact: {
    writes: "the person's record",
    method: "PATCH",
    path: (contactId) => `/api/contacts/${encodeURIComponent(contactId)}`,
  },
};

/**
 * A typed-in answer in the shape the endpoint takes. "" means the person
 * cleared it, which every writer here spells as null.
 */
export function encodeAnswer(field: CleanupAskedField, raw: string): string | number | null {
  if (raw === "") return null;
  if (field.input === "yesno") return raw === "1" || raw === "yes" || raw === "true" ? 1 : 0;
  return raw;
}

/** The value a control shows for what is on file, in the control's own
 *  wire format ("" for not answered, "1"/"0" for yesno). */
export function currentAsControlValue(field: CleanupAskedField): string {
  if (field.current === null) return "";
  return String(field.current);
}

/** "Not answered", or the current value in the person's words. */
export function currentAsWords(field: CleanupAskedField): string | null {
  if (field.current === null) return null;
  if (field.input === "yesno") return Number(field.current) === 1 ? "Yes" : "No";
  if (field.input === "select") {
    return field.options?.find((o) => o.value === String(field.current))?.label ?? String(field.current);
  }
  return String(field.current);
}
